import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithGoogle, useAuthState } from '../../../../../utils/firebase';
import { loginClient } from '../../../../../crud/clients_api/clients';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import './ClientAuth.scss';
import Logo from './Logo';
import { FaCheck, FaGoogle, FaArrowLeft } from 'react-icons/fa';
import ClientLogin from './components/ClientLogin/ClientLogin';
import NewClient from './components/NewClient/NewClient';

interface User {
  accessToken: string;
  email: string;
  name: string;
  photoURL: string;
}

const ClientAuth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { '*': destination } = useParams();
  const [user] = useAuthState();
  const [error, setError] = useState('');
  const [goToLogin, setGoToLogin] = useState(false);
  const [goToNewClient, setGoToNewClient] = useState(false);

  // Store local_id from URL params if present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const localId = searchParams.get('local_id');
    if (localId) {
      sessionStorage.setItem('pending_local_id', localId);
    }
  }, [location]);

  useEffect(() => {
    // Check localStorage for existing client session
    const token = localStorage.getItem('access_token');
    const client = localStorage.getItem('client');
    if (token && client) {
      navigate(`/${destination || 'ClientPortal/Home'}`);
    }
  }, [navigate, destination]);

  // Handle Google authentication
  useEffect(() => {
    if (user) {
      const typedUser = user as User;
      
      loginClient(null, null, typedUser.accessToken, "google")
        .then((response) => {
          if ((response as any).status === 200) {
            localStorage.setItem('access_token', JSON.stringify((response as any).data.token));
            localStorage.setItem('client', JSON.stringify((response as any).data));
            navigate(`/${destination || 'ClientPortal/Home'}`);
          } else {
            setError('Error al iniciar sesión');
          }
        })
        .catch((err) => {
          setError('Error al iniciar sesión');
          console.error("ERROR ->", err);
        });
    }
  }, [user, navigate, destination]);

  const handleGoogleLogin = () => {
    setError(''); // Clear previous errors
    
    try {
      signInWithGoogle();
    } catch (error: any) {
      console.error('Google Sign-In Error:', error);
      if (error?.code === 'auth/popup-blocked') {
        setError('Tu navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio.');
      } else {
        setError('Error al iniciar sesión con Google. Intenta de nuevo.');
      }
    }
  };

  const handleLoginOption = () => {
    setGoToLogin(true);
    setGoToNewClient(false);
  };

  const handleNewClient = () => {
    setGoToNewClient(true);
    setGoToLogin(false);
  };

  const handleBack = () => {
    setGoToLogin(false);
    setGoToNewClient(false);
    setError('');
  };

  const handleContinueWithoutAccount = () => {
    navigate(`/${destination || 'ClientPortal/Home'}`);
  };

  const showMainOptions = !goToLogin && !goToNewClient;

  return (
    <div className="client-auth-container">
      <div className="client-auth-content">
        <div className="auth-logo-section">
          {(goToLogin || goToNewClient) && (
            <button className="back-button-inline" onClick={handleBack}>
              <FaArrowLeft /> Volver
            </button>
          )}
          <Logo className="auth-logo" />
        </div>

        {showMainOptions && (
          <>
            <div className="benefits-card">
              <h3 className="benefits-title">Házte miembro</h3>
              <ul className="benefits-list">
                <li>
                  <FaCheck className="check-icon" />
                  <span>Guarda tus restaurantes favoritos</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Obtén descuentos exclusivos</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Acumula puntos en cada visita</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Accede a beneficios exclusivos</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>y mucho más...</span>
                </li>
              </ul>
            </div>

            <div className="auth-actions">
              <Button 
                className="auth-btn auth-btn-primary" 
                onClick={handleLoginOption}
              >
                Ingresar
              </Button>
              
              <Button 
                className="auth-btn auth-btn-google" 
                onClick={handleGoogleLogin}
              >
                <FaGoogle className="google-icon" />
                Continuar con Google
              </Button>

              <p className="divider-text">¿Aún no tienes cuenta?</p>

              <Button 
                className="auth-btn auth-btn-secondary" 
                onClick={handleNewClient}
              >
                Crear Cuenta
              </Button>

              <Button 
                className="auth-btn auth-btn-link" 
                onClick={handleContinueWithoutAccount}
              >
                Continuar sin crear cuenta
              </Button>
            </div>
          </>
        )}

        {(goToLogin || goToNewClient) && (
          <div className="auth-form-wrapper">
            <h2 className="form-title">
              {goToLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </h2>

            {goToLogin && <ClientLogin destination={destination} navigate={navigate} />}
            {goToNewClient && <NewClient destination={destination} navigate={navigate} />}
          </div>
        )}

        {error && <p className="error-message">{error}</p>}
        
        {error && error.includes('ventana emergente') && (
          <div className="popup-help-message">
            <p><strong>¿Cómo permitir ventanas emergentes?</strong></p>
            <ul>
              <li>Busca el ícono de ventana bloqueada 🚫 en la barra de direcciones de tu navegador</li>
              <li>Haz clic en el ícono y selecciona "Permitir ventanas emergentes"</li>
              <li>Recarga la página e intenta iniciar sesión con Google nuevamente</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientAuth;
